import { defineStore } from 'pinia'
import sls from '@/libs/secure-ls';
import { setToken } from '@/libs/axios';

let storedData = {}
try {
  const authData = sls.get('p-auth')
  storedData = JSON.parse(authData || '{}')
} catch (error) {
  console.warn('Failed to read auth data from secure storage, clearing corrupted data:', error)
  // Clear corrupted data
  try {
    sls.remove('p-auth')
  } catch (removeError) {
    console.warn('Failed to remove corrupted auth data:', removeError)
  }
  storedData = {}
}

export const useAuthStore = defineStore({
  id: 'auth',
  state: () => ({
    permissions: storedData?.permissions || [],
    roles: storedData?.roles || [],
    token: storedData?.token || null,
    refreshToken: storedData?.refreshToken || null,
    userData: storedData?.userData || null,
    userId: storedData?.userId || null,
    authReady: !!(storedData?.userData && storedData?.permissions?.length),
  }),
  getters: {
    entity() {
      return this.userData?.entity?.entity ?? {
        logo: '',
        name: '',
        address: '',
        phone: '',
      };
    },
    subscriptionStatus() {
      return this.userData?.entity?.entity?.subscription_status || 'none'
    },
    hasActiveSubscription() {
      return ['active', 'warning', 'trial'].includes(this.subscriptionStatus)
    },
    isSubscriptionReadOnly() {
      return this.subscriptionStatus === 'grace'
    },
    isSubscriptionLocked() {
      return ['suspended', 'cancelled', 'none'].includes(this.subscriptionStatus)
    },
    onboardingStatus() {
      return this.userData?.entity?.entity?.onboarding_status || 'registered'
    },
    isOnboardingComplete() {
      return this.onboardingStatus === 'completed'
    },
    /**
     * Onboarding lock: menus locked when onboarding is not completed
     * AND subscription is active (so we don't double-lock with subscription lock).
     */
    isOnboardingLocked() {
      const onb = this.onboardingStatus
      const sub = this.subscriptionStatus
      // Only apply onboarding lock when subscription is active but onboarding not done
      if (['active'].includes(sub) && onb !== 'completed') {
        return true
      }
      return false
    },
  },
  actions: {
    ableTo(action, perm) {
      if(!Array.isArray(perm)) perm = [perm]
      if (action === 'read') action = 'view'
      perm = [...perm].map((v) => `${action} ${v}`)
      return this.permissions.filter(({subject}) => perm.includes(subject)).length > 0
    },
    setAuthReady(ready) {
      this.authReady = ready
    },
    signOut() {
      this.permissions = []
      this.roles = []
      this.token = null
      this.refreshToken = null
      this.userData = null
      this.userId = null
      this.authReady = false
      this.updateStorage()
      setToken(null)
    },
    setRole(role) {
      this.roles = [...role]
      this.updateStorage()
    },
    setPermissions(permissions) {
      this.permissions = [...permissions]
      this.updateStorage()
    },
    setToken({access_token, refreshToken}) {
      this.token = access_token
      this.refreshToken = refreshToken
      this.updateStorage()
      setToken(access_token)
    },
    setUserData(userData) {
      this.userId = userData.id
      this.userData = userData
      this.updateStorage()
    },
    updateSubscriptionStatus(status) {
      if (this.userData?.entity?.entity) {
        // Use spread to create new object reference for Vue reactivity
        this.userData = {
          ...this.userData,
          entity: {
            ...this.userData.entity,
            entity: {
              ...this.userData.entity.entity,
              subscription_status: status,
            },
          },
        }
        this.updateStorage()
      }
    },
    /**
     * Update entity data (after facility profile update)
     * @param {Object} entityData - The updated entity data
     */
    updateEntity(entityData) {
      if (this.userData?.entity?.entity) {
        // Merge the new entity data with existing data
        this.userData = {
          ...this.userData,
          entity: {
            ...this.userData.entity,
            entity: {
              ...this.userData.entity.entity,
              ...entityData,
            },
          },
        }
        this.updateStorage()
      }
    },
    updateStorage() {
      sls.set('p-auth', JSON.stringify({
        permissions: this.permissions,
        roles: this.roles,
        token: this.token,
        refreshToken: this.refreshToken,
        userData: this.userData,
        userId: this.userId,
      }))
    }
  },
})
